import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

import { STORAGE_KEYS } from '@/shared/constants/app';
import { ToastService } from '@/shared/utilities/feedback/toast-service';
import storageService from '@/shared/services/storage.service';
import { FirebaseMessaging, isFirebaseInitialized } from '@/shared/plugins/firebase-messaging';
import { getPushSubscriptionPayload } from '@/shared/services/push-device.service';

const PUSH_PERMISSION = {
  GRANTED: 'granted',
  PROMPT: 'prompt',
};
const REGISTRATION_TIMEOUT_MS = 10000;

// Lazy import breaks a circular dependency: firebase-push.service -> base.service ->
// repository.factory -> ...repositories -> rest-client -> auth.store -> this service ->
// firebase-push.service.
const getFirebasePushService = () =>
  import('@/shared/services/firebase-push.service').then((module) => module.default);

const isNativePlatform = () => Capacitor.getPlatform() !== 'web';

let listenersAttached = false;
let sessionListenerHandles = [];
let resolveRegistration = null;
let sessionWork = Promise.resolve();
let registeredFcmToken = null;
let registeredSession = null;

// Device cleanup must finish before a new account subscribes on the same
// installation. Each operation also keeps the JWT of its original account.
const enqueueSessionWork = (action) => {
  const result = sessionWork.then(action, action);
  sessionWork = result.catch(() => {});
  return result;
};

const getRegisteredFcmToken = () =>
  registeredFcmToken ?? storageService.getItem(STORAGE_KEYS.PUSH_FCM_TOKEN);
const setCachedFcmToken = async (fcmToken) => {
  await storageService.setItem(STORAGE_KEYS.PUSH_FCM_TOKEN, fcmToken);
};
const clearCachedFcmToken = async () => {
  await storageService.removeItem(STORAGE_KEYS.PUSH_FCM_TOKEN);
};

const resolveApnsRegistration = (apnsToken = '') => {
  resolveRegistration?.(apnsToken);
};

const createRegistrationWaiter = () => {
  let cancel;
  const promise = new Promise((resolve) => {
    const resolver = (apnsToken) => {
      window.clearTimeout(timeout);
      resolveRegistration = null;
      resolve(apnsToken);
    };

    const timeout = window.setTimeout(() => resolver(''), REGISTRATION_TIMEOUT_MS);
    cancel = () => resolver('');
    // Session operations are serialized, so only one APNs waiter can be active.
    resolveRegistration = resolver;
  });

  return { promise, cancel };
};

const attachRegistrationListenersOnce = async () => {
  if (listenersAttached) return;

  // Native listeners must be ready before register() can emit either outcome.
  try {
    const registrationHandle = await PushNotifications.addListener('registration', (event) => {
      const apnsToken = event?.value || '';
      if (typeof apnsToken === 'string' && apnsToken) {
        resolveApnsRegistration(apnsToken);
      }
    });
    sessionListenerHandles.push(registrationHandle);

    const registrationErrorHandle = await PushNotifications.addListener('registrationError', () => {
      resolveApnsRegistration('');
    });

    sessionListenerHandles.push(registrationErrorHandle);
    listenersAttached = true;
  } catch (error) {
    // A partial attachment must not leave duplicate listeners on the next login.
    const handles = sessionListenerHandles;
    sessionListenerHandles = [];
    await Promise.all(handles.map((handle) => handle.remove()));
    throw error;
  }
};

const registerCurrentDevice = async (fcmToken, context) => {
  if (!fcmToken) return;

  // Skip unchanged foreground checks only after this runtime's server acceptance.
  // A stored token alone cannot establish the current account's ownership.
  if (
    registeredFcmToken === fcmToken &&
    registeredSession?.registrationId === context.registrationId &&
    registeredSession?.apiUrl === context.apiUrl
  ) {
    return;
  }

  try {
    const payload = await getPushSubscriptionPayload(fcmToken);
    if (!payload) return;
    if (!context.isCurrent()) return;

    // Pin registration to the session that scheduled it, not a later login.
    const firebasePushService = await getFirebasePushService();
    if (!context.isCurrent()) return;
    await firebasePushService.subscribe(payload, {
      baseURL: context.apiUrl,
      headers: { Authorization: `Bearer ${context.token}` },
    });

    // Retain accepted ownership even if persisting the token fails.
    registeredFcmToken = fcmToken;
    registeredSession = { registrationId: context.registrationId, apiUrl: context.apiUrl };
    await setCachedFcmToken(fcmToken);
  } catch {
    if (context.isCurrent()) {
      ToastService.error(
        ToastService.t(
          'toast.pushRegistrationFailed',
          'Failed to enable push notifications. Please try again.',
        ),
      );
    }
  }
};

const ensurePermissionAndRegisterForPush = async () => {
  let permStatus = await PushNotifications.checkPermissions();
  if (permStatus?.receive === PUSH_PERMISSION.PROMPT) {
    permStatus = await PushNotifications.requestPermissions();
  }
  if (permStatus?.receive !== PUSH_PERMISSION.GRANTED) return false;

  // The app bridge retrieves Android's FCM token below. Avoid a second token
  // request and registration event through Capacitor's register() method.
  if (Capacitor.getPlatform() === 'android') return true;

  // iOS still needs APNs before FCM registration. Capture immediate callbacks.
  await attachRegistrationListenersOnce();
  const registration = createRegistrationWaiter();
  try {
    await PushNotifications.register();
    const apnsToken = await registration.promise;
    if (!apnsToken) {
      throw new Error('Push notification registration failed or timed out');
    }

    return true;
  } finally {
    // Also clear the timer and waiter when register() itself rejects.
    registration.cancel();
  }
};

const getFcmToken = async () => {
  const firebaseInitialized = await isFirebaseInitialized();
  if (!firebaseInitialized) {
    console.warn('Firebase is not initialized; push registration was not started');
    return '';
  }

  const isRegistered = await ensurePermissionAndRegisterForPush();
  if (!isRegistered) return '';

  // Ask the native SDK for its current token; a saved token may have rotated.
  const result = await FirebaseMessaging.register();
  return result?.fcmToken || '';
};

const registerForSession = (context) =>
  enqueueSessionWork(async () => {
    if (!context.isCurrent()) return;

    if (!isNativePlatform()) return;

    // Check on login and foreground return; denied permission leaves login unaffected.
    const fcmToken = await getFcmToken();

    // Permission prompts and token retrieval may finish after the patient logs out.
    if (!context.isCurrent()) return;
    await registerCurrentDevice(fcmToken, context);
  });

const unregisterForSession = ({ token, apiUrl }) =>
  enqueueSessionWork(async () => {
    // Use the retired session's JWT, never the next patient's credentials.
    try {
      const fcmToken = await getRegisteredFcmToken();
      if (fcmToken) {
        const firebasePushService = await getFirebasePushService();
        await firebasePushService.unsubscribe(fcmToken, {
          baseURL: apiUrl,
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } finally {
      // Local device state must be cleared even when server cleanup fails.
      registeredFcmToken = null;
      registeredSession = null;
      try {
        await clearCachedFcmToken();
      } finally {
        // Reset before removal so a failed native or storage operation cannot
        // leave attachment state blocking the next queued login.
        const handles = sessionListenerHandles;
        sessionListenerHandles = [];
        listenersAttached = false;
        await Promise.all(handles.map((handle) => handle.remove()));
      }
    }
  });

export default { registerForSession, unregisterForSession };
